import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Edit, Trash2, User, Crown, Shield, Users, X, AlertCircle, Search } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import StyledDropdown from './StyledDropdown';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [roles, setRoles] = useState([]);
  const [reportingUsers, setReportingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roleId: '',
    groupId: '',
    reportingUserId: '',
  });
  const [userSearch, setUserSearch] = useState('');

  // Role icons mapping
  const roleIcons = {
    'Superadmin': Crown,
    'Admin': Shield,
    'Group Leader': Users,
    'Team Leader': User,
    'Agent': User,
  };

  const roleColors = {
    'Superadmin': 'text-purple-600 dark:text-purple-400',
    'Admin': 'text-primary-600 dark:text-primary-400',
    'Group Leader': 'text-green-600 dark:text-green-400',
    'Team Leader': 'text-yellow-600 dark:text-yellow-400',
    'Agent': 'text-gray-600 dark:text-dark-muted',
  };

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch users, groups, and roles in parallel
      const [usersRes, groupsRes, rolesRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/users`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/groups`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/roles`, { headers: getAuthHeaders() }),
      ]);

      const usersResult = await usersRes.json();
      const groupsResult = await groupsRes.json();
      const rolesResult = await rolesRes.json();

      if (usersResult.success) {
        setUsers(usersResult.data);
      }

      if (groupsResult.success) {
        setGroups(groupsResult.data);
      }

      if (rolesResult.success) {
        setRoles(rolesResult.data);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch reporting users (Team Leaders + Group Leader) when group changes
  const fetchReportingUsers = async (groupId) => {
    if (!groupId) {
      setReportingUsers([]);
      return;
    }

    try {
      const response = await fetch(`${getApiUrl()}/api/users/reporting-users/${groupId}`, {
        headers: getAuthHeaders(),
      });
      const result = await response.json();

      if (result.success) {
        setReportingUsers(result.data);
      }
    } catch (err) {
      console.error('Fetch reporting users error:', err);
      setReportingUsers([]);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setError('');

      // Validate required fields based on role
      const selectedRole = roles.find((r) => r.id === parseInt(formData.roleId));
      if (!selectedRole) {
        setError('Please select a role');
        return;
      }

      if (selectedRole.name === 'Group Leader' || selectedRole.name === 'Team Leader' || selectedRole.name === 'Agent') {
        if (!formData.groupId) {
          setError('Group is required for this role');
          return;
        }
      }

      if (selectedRole.name === 'Agent' && !formData.reportingUserId) {
        setError('Reporting user (Team Leader or Group Leader) is required for Agent role');
        return;
      }

      const response = await fetch(`${getApiUrl()}/api/users`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          roleId: parseInt(formData.roleId),
          groupId: formData.groupId ? parseInt(formData.groupId) : null,
          reportingUserId: formData.reportingUserId || null,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setFormData({
          name: '',
          email: '',
          password: '',
          roleId: '',
          groupId: '',
          reportingUserId: '',
        });
        setShowCreateModal(false);
        fetchData();
      } else {
        setError(result.message || 'Failed to create user');
      }
    } catch (err) {
      console.error('Create user error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      roleId: user.roleId.toString(),
      groupId: user.group?.id?.toString() || '',
      reportingUserId: user.reportingUser?.id || '',
    });
    if (user.group?.id) {
      fetchReportingUsers(user.group.id.toString());
    }
    setShowEditModal(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      setError('');

      const updatePayload = {
        name: formData.name,
        email: formData.email,
        status: editingUser.status,
      };

      if (formData.password) {
        updatePayload.password = formData.password;
      }

      // Only update role/group if changed
      if (parseInt(formData.roleId) !== editingUser.roleId) {
        updatePayload.roleId = parseInt(formData.roleId);
      }

      if (formData.groupId !== (editingUser.group?.id?.toString() || '')) {
        updatePayload.groupId = formData.groupId ? parseInt(formData.groupId) : null;
      }

      if (formData.reportingUserId !== (editingUser.reportingUser?.id || '')) {
        updatePayload.reportingUserId = formData.reportingUserId || null;
      }

      const response = await fetch(`${getApiUrl()}/api/users/${editingUser.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updatePayload),
      });

      const result = await response.json();

      if (result.success) {
        setShowEditModal(false);
        setEditingUser(null);
        setFormData({
          name: '',
          email: '',
          password: '',
          roleId: '',
          groupId: '',
          reportingUserId: '',
        });
        fetchData();
      } else {
        setError(result.message || 'Failed to update user');
      }
    } catch (err) {
      console.error('Update user error:', err);
      setError('Unable to connect to server');
    }
  };

  const handleDeleteUser = async (id) => {
    try {
      setError('');
      const response = await fetch(`${getApiUrl()}/api/users/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setDeleteConfirm(null);
        fetchData();
      } else {
        setError(result.message || 'Failed to delete user');
        setDeleteConfirm(null);
      }
    } catch (err) {
      console.error('Delete user error:', err);
      setError('Unable to connect to server');
      setDeleteConfirm(null);
    }
  };

  const getRoleInfo = (roleName) => {
    const Icon = roleIcons[roleName] || User;
    const color = roleColors[roleName] || 'text-gray-600 dark:text-dark-muted';
    return { Icon, color, name: roleName };
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        status === 'Active' 
          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
      }`}>
        {status}
      </span>
    );
  };

  const getInitials = (name) => {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const selectedRole = roles.find((r) => r.id === parseInt(formData.roleId));
  const needsGroup = selectedRole && ['Group Leader', 'Team Leader', 'Agent'].includes(selectedRole.name);
  const needsTeamLeader = selectedRole && selectedRole.name === 'Agent';

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const roleName = typeof u.role === 'string' ? u.role : u.role?.name || '';
      const haystack = [
        u.name,
        u.email,
        u.accountId,
        roleName,
        u.group?.name,
        u.reportingUser?.name,
        u.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [users, userSearch]);

  const {
    currentPage,
    paginatedItems: paginatedUsers,
    totalItems,
    handlePageChange,
    resetPage,
  } = usePagination(filteredUsers, 10);

  useEffect(() => {
    resetPage();
  }, [userSearch, resetPage]);

  const renderModal = (isEdit = false) => {
    const modalTitle = isEdit ? 'Edit User' : 'Add New User';
    const submitLabel = isEdit ? 'Update User' : 'Add User';
    const onSubmit = isEdit ? handleUpdateUser : handleCreateUser;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-dark-card rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto transition-colors duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">{modalTitle}</h3>
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditModal(false);
                  setEditingUser(null);
                } else {
                  setShowCreateModal(false);
                }
                setFormData({
                  name: '',
                  email: '',
                  password: '',
                  roleId: '',
                  groupId: '',
                  reportingUserId: '',
                });
                setError('');
              }}
              className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={onSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Enter full name"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Enter email address"
                  required
                />
              </div>

              {!isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Enter password (min 6 characters)"
                    required={!isEdit}
                    minLength={6}
                  />
                </div>
              )}

              {isEdit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    New Password (leave blank to keep current)
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                    placeholder="Enter new password (min 6 characters)"
                    minLength={6}
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Role *
                </label>
                <StyledDropdown
                  options={[
                    { value: '', label: 'Select a role' },
                    ...roles.map(r => ({ value: r.id.toString(), label: r.name }))
                  ]}
                  value={formData.roleId}
                  onChange={(val) => {
                    setFormData({
                      ...formData,
                      roleId: val,
                      groupId: '',
                      reportingUserId: '',
                    });
                    if (val) {
                      const role = roles.find((r) => r.id === parseInt(val));
                      if (role && ['Group Leader', 'Team Leader', 'Agent'].includes(role.name)) {
                        // Keep groupId if it's still valid
                      } else {
                        setFormData((prev) => ({ ...prev, groupId: '', reportingUserId: '' }));
                      }
                    }
                  }}
                  placeholder="Select a role"
                  className="w-full"
                />
              </div>
              
              {needsGroup && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Group *
                  </label>
                  <StyledDropdown
                    options={[
                      { value: '', label: 'Select a group' },
                      ...groups.map(g => ({ value: g.id.toString(), label: g.name }))
                    ]}
                    value={formData.groupId}
                    onChange={(val) => {
                      setFormData({
                        ...formData,
                        groupId: val,
                        reportingUserId: '',
                      });
                      if (val) {
                        fetchReportingUsers(val);
                      } else {
                        setReportingUsers([]);
                      }
                    }}
                    placeholder="Select a group"
                    icon={Users}
                    className="w-full"
                  />
                </div>
              )}
              
              {needsTeamLeader && formData.groupId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                    Reports To (Team Leader or Group Leader) *
                  </label>
                  <StyledDropdown
                    options={[
                      { value: '', label: 'Select reporting user' },
                      ...reportingUsers.map(u => ({ 
                        value: u.id.toString(), 
                        label: `${u.name} (${typeof u.role === 'string' ? u.role : u.role?.name || 'N/A'})` 
                      }))
                    ]}
                    value={formData.reportingUserId}
                    onChange={(val) => setFormData({...formData, reportingUserId: val})}
                    placeholder="Select reporting user"
                    icon={User}
                    className="w-full"
                  />
                </div>
              )}

              {/* Role Assignment Logic Display */}
              {selectedRole && (
                <div className="bg-primary-50 dark:bg-primary-900/20 p-3 rounded-md">
                  <p className="text-xs text-primary-800 dark:text-primary-300">
                    <strong>Assignment Logic:</strong>
                    {selectedRole.name === 'Group Leader' && ' Will become leader of selected group'}
                    {selectedRole.name === 'Team Leader' && ' Will report to the group leader'}
                    {selectedRole.name === 'Agent' && ' Will report to selected Team Leader or Group Leader'}
                    {['Admin', 'Superadmin'].includes(selectedRole.name) && ' Backend control role - no group assignment needed'}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  if (isEdit) {
                    setShowEditModal(false);
                    setEditingUser(null);
                  } else {
                    setShowCreateModal(false);
                  }
                  setFormData({
                    name: '',
                    email: '',
                    password: '',
                    roleId: '',
                    groupId: '',
                    reportingUserId: '',
                  });
                  setError('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-dark-text border border-gray-300 dark:border-dark-border rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors duration-200"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 dark:text-primary-400 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-gray-600 dark:text-dark-muted">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Error Message */}
      {error && !showCreateModal && !showEditModal && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Action Button */}
      <div className="flex items-center justify-end mb-6">
        <button
          onClick={() => {
            setShowCreateModal(true);
            setFormData({
              name: '',
              email: '',
              password: '',
              roleId: '',
              groupId: '',
              reportingUserId: '',
            });
            setError('');
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add User</span>
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden transition-colors duration-200">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-dark-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text">All Users</h3>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-dark-muted pointer-events-none" />
            <input
              type="search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search name, email, role, group…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-dark-border rounded-lg bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text placeholder:text-gray-400 dark:placeholder:text-dark-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Search users"
            />
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
            <thead className="bg-gray-50 dark:bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Reports To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-dark-border">
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500 dark:text-dark-muted">
                    {userSearch.trim() ? 'No users match your search.' : 'No users yet.'}
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => {
                  const roleName = typeof user.role === 'string' ? user.role : user.role?.name || 'N/A';
                  const roleInfo = getRoleInfo(roleName);
                  const RoleIcon = roleInfo.Icon;

                  return (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-sm font-medium text-primary-700">
                              {getInitials(user.name)}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{user.name}</div>
                            <div className="text-sm text-gray-500 dark:text-dark-muted">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <RoleIcon className={`w-4 h-4 ${roleInfo.color}`} />
                          <span className="text-sm text-gray-900 dark:text-dark-text">{roleName}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-dark-text">{user.group?.name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-dark-text">
                          {user.reportingUser?.name || (
                            <span className="text-gray-400 dark:text-dark-muted italic">None</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            type="button"
                            onClick={() => handleEditClick(user)}
                            className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteConfirm(user.id)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Create Modal */}
      {showCreateModal && renderModal(false)}

      {/* Edit Modal */}
      {showEditModal && renderModal(true)}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 w-full max-w-md transition-colors duration-200">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4">Delete User</h3>
            <p className="text-gray-600 dark:text-dark-muted mb-6">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 dark:text-dark-text border border-gray-300 dark:border-dark-border rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
